import { Injectable, inject } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { RouterStateSnapshot, TitleStrategy } from "@angular/router";
import { DASHBOARD_TYPE } from "@front/m19-injection-token";
import { AccountSelectionResolver } from "@front/m19-services";
import { AdminLoginComponent, AuthLayoutComponent, SsoLoginComponent } from "@front/m19-ui";
import { dashboardRoutes } from "@m19-board/app-dashboard.routes";
import { authRoutes } from "@m19-board/auth/auth.routes";
import { ProductTourComponent } from "@m19-board/auth/product-tour/product-tour.component";
import { ProfileComponent } from "@m19-board/auth/profile-page/profile.component";
import { authGuard } from "@m19-board/guards/auth-guard/auth.guard";
import { onboardingGuard } from "@m19-board/onboarding/guards/onboarding.guard";
import { OnboardingLayoutComponent } from "@m19-board/onboarding/layout/onboarding-layout.component";
import { onboardingRoutes } from "@m19-board/onboarding/onboarding.routes";
import { M19Route } from "@m19-board/utils/M19Route";
import { BoardLayoutComponent } from "./containers/board-layout/board-layout.component";
import { Dashboard360LayoutComponent } from "./containers/dashboard360-layout/dashboard360-layout.component";
import { suspensionGuard } from "./guards/suspension/suspension.guard";
import { LayoutSelectorService } from "./layout-selector.service";
import { SuspensionComponent } from "./suspension/suspension.component";

export const routes: M19Route[] = [
  {
    path: "",
    redirectTo: "dashboard",
    pathMatch: "full",
  },
  {
    path: "",
    component: AuthLayoutComponent,
    children: authRoutes,
  },
  {
    path: "admin-login",
    component: AdminLoginComponent,
    title: "Admin Login",
  },
  {
    path: "sso-login",
    component: SsoLoginComponent,
    title: "Login",
  },
  {
    path: "product-tour",
    component: ProductTourComponent,
    title: "Product Tour",
  },
  {
    path: "dashboard360",
    component: Dashboard360LayoutComponent,
    data: {
      withAccountSelector: true,
      withDatePicker: true,
      withCurrencySelector: true,
    },
    providers: [
      {
        provide: DASHBOARD_TYPE,
        useValue: "dashboard360",
      },
    ],
    canActivate: [authGuard, suspensionGuard],
    resolve: [AccountSelectionResolver],
    children: [
      {
        path: "",
        redirectTo: "account360",
        pathMatch: "full",
      },
      {
        path: "account360",
        loadChildren: () =>
          import("./account360/account360.routes").then((r) => r.A360Routes("/dashboard360/account360/dashboard-list")),
        title: "Account 360",
      },
      {
        path: "product360",
        loadChildren: () =>
          import("./product360/product360.routes").then((r) => r.P360Routes("/dashboard360/product360/dashboard-list")),
        title: "Product 360",
      },
      {
        path: "profile",
        component: ProfileComponent,
        title: "Profile",
        data: {
          parentTitle: "page-title.my_profile",
        },
      },
    ],
  },
  {
    path: "",
    component: BoardLayoutComponent,
    canActivate: [authGuard],
    resolve: [AccountSelectionResolver],
    children: [
      {
        path: "",
        canActivate: [suspensionGuard, onboardingGuard],
        children: dashboardRoutes,
      },
    ],
  },
  {
    path: "onboarding",
    component: OnboardingLayoutComponent,
    canActivate: [authGuard],
    children: onboardingRoutes,
  },
  {
    path: "suspension",
    component: SuspensionComponent,
    canActivate: [authGuard],
  },
  { path: "**", redirectTo: "dashboard" },
];

@Injectable()
export class TemplatePageTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly layoutSelectorService = inject(LayoutSelectorService);

  override updateTitle(routerState: RouterStateSnapshot) {
    const title = this.buildTitle(routerState);
    const pageTitle = this.layoutSelectorService.getPageTitle();
    if (title !== undefined) {
      this.title.setTitle(`${pageTitle} - ${title}`);
    } else {
      this.title.setTitle(pageTitle);
    }
  }
}
